import { XYCoord } from 'react-dnd';
import { isEqual } from 'lodash';
import { isEmptyId, ILayout, ILayoutComponent } from '@diana/core';
import {
  IDndLayoutComponentLocation as ILocation,
  IDndLayoutComponentDragObject as IDragObject,
  IDndLayoutComponentDropResult as IDropResult,
} from './DndLayoutComponents.types';

export type GridPosition = { row: number; col: number };

export type GridDirection = 'top' | 'right' | 'bottom' | 'left';

export type ComponentsGrid = ILayoutComponent[][];

/**
 * Parses the column width size of a layout component
 */
export const getColumnWidthSize = (layoutComponent: ILayoutComponent) => {
  return parseInt(layoutComponent.properties.width?.split('-')[1] || '12');
};

/**
 * Simulates the grid of how the layout components will be rendered by using a 2 dimensional array.
 * 1st Dimension represents rows, while 2nd dimension represents columns.
 */
export const buildComponentsGrid = (components: ILayoutComponent[]): ComponentsGrid => {
  const grid: ComponentsGrid = [];
  let column: ILayoutComponent[] = [];
  for (const component of components) {
    const occupiedSpace = column.reduce((sum, lc) => sum + getColumnWidthSize(lc), 0);
    const componentSize = getColumnWidthSize(component);
    if (occupiedSpace + componentSize > 12) {
      grid.push(column);
      column = [];
    }
    column.push(component);
  }
  if (column.length) grid.push(column);
  return grid;
};

/**
 * Identifies the row and column position of layout component in the given grid.
 */
export const getComponentGridPosition = (args: {
  componentId: string;
  grid: ComponentsGrid;
}): GridPosition | null => {
  const { componentId, grid } = args;
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      if (grid[row][col].id === componentId) return { row, col };
    }
  }
  return null;
};

/**
 * Identifies the adjacent direction of target from source in the given grid.
 */
export const getComponentGridDirection = (args: {
  sourceId: string;
  targetId: string;
  grid: ComponentsGrid;
}): GridDirection | null => {
  const { sourceId, targetId, grid } = args;
  const position1 = getComponentGridPosition({ componentId: sourceId, grid });
  const position2 = getComponentGridPosition({ componentId: targetId, grid });
  if (position1 && position2) {
    if (position1.row === position2.row) return position1.col < position2.col ? 'right' : 'left';
    if (position1.col === position2.col) return position1.row < position2.row ? 'bottom' : 'top';
  }
  return null;
};

export const areInTheSameGrid = (location1: ILocation, location2: ILocation) => {
  if (!isEqual(location1.layoutId, location2.layoutId)) {
    return false;
  }

  if (isEmptyId(location1.parentId) && isEmptyId(location2.parentId)) {
    return true;
  }

  if (!isEmptyId(location1.parentId) && !isEmptyId(location2.parentId)) {
    return isEqual(location1.parentId, location2.parentId);
  }

  return false;
};

/**
 * Checks if layout component can be dropped to the target drop result
 */
export const canMoveLayoutComponent = (args: {
  dragObject: IDragObject;
  targetDropResult: IDropResult;
  targetLayout: ILayout | null;
  targetElement: HTMLElement | null;
  clientOffset: XYCoord | null;
}) => {
  const { dragObject, targetDropResult, clientOffset, targetElement, targetLayout } = args;

  const dropRect = targetElement?.getBoundingClientRect();

  // already in position
  if (isEqual(dragObject.location, targetDropResult.location)) {
    return false;
  }

  // check pointer and drop rect dom positions
  if (dragObject.location && targetDropResult.componentId && clientOffset && dropRect) {
    if (targetLayout && areInTheSameGrid(dragObject.location, targetDropResult.location)) {
      // index of source and target in their level
      const sourceIndex = dragObject.location.index;
      const targetIndex = targetDropResult.location.index;

      // width size of source and target
      const sourceSize = getColumnWidthSize(dragObject.component);
      const targetSize = targetDropResult.componentSize;

      // get components in same level
      const components = targetLayout.components.filter((c) => {
        return isEmptyId(targetDropResult.location.parentId)
          ? isEmptyId(c.parentId)
          : isEqual(c.parentId, targetDropResult.location.parentId);
      });

      // get the direction of target from source
      const direction = getComponentGridDirection({
        sourceId: dragObject.component.id,
        targetId: targetDropResult.componentId,
        grid: buildComponentsGrid(components),
      });

      // determine rectangle on screen
      const targetWidth = dropRect.right - dropRect.left;
      const targetHeight = dropRect.bottom - dropRect.top;

      const widthOffset = Math.min(20, targetWidth / 2);
      const heightOffset = Math.min(20, targetHeight / 2);

      // determine mouse position
      const pointerOffset = clientOffset ?? { x: 0, y: 0 };
      const hoverClientX = pointerOffset.x - dropRect.left;
      const hoverClientY = pointerOffset.y - dropRect.top;

      // when source and target are vertically adjacent
      // cursor must pass at least half of the height of the target
      if (direction === 'top' || direction === 'bottom') {
        if (sourceIndex < targetIndex && hoverClientY < targetHeight / 2) return false;
        if (sourceIndex > targetIndex && hoverClientY > targetHeight / 2) return false;
      }

      // source and target are horizontally adjacent
      if (direction === 'left' || direction === 'right') {
        // when source and target does NOT have the same size,
        // cursor must at least pass half the width of the target
        if (sourceSize !== targetSize) {
          if (sourceIndex < targetIndex && hoverClientX < targetWidth / 2) return false;
          if (sourceIndex > targetIndex && hoverClientX > targetWidth / 2) return false;
        }

        // when source and target have the same size,
        // cursor must at least pass 20px from the edge of target
        if (sourceSize === targetSize) {
          if (sourceIndex < targetIndex && hoverClientX < widthOffset) return false;
          if (sourceIndex > targetIndex && hoverClientX > targetWidth - widthOffset) return false;
        }
      }

      // if the direction of dragged item cannot be identified
      // cursor must pass 20px from the edge of the target
      if (!direction) {
        if (sourceIndex < targetIndex && hoverClientY < heightOffset) return false;
        if (sourceIndex > targetIndex && hoverClientY > targetHeight - heightOffset) return false;
        if (sourceIndex < targetIndex && hoverClientX < widthOffset) return false;
        if (sourceIndex > targetIndex && hoverClientX > targetWidth - widthOffset) return false;
      }
    }
  }

  return true;
};
